import { z } from "zod";
import { isoDate, isoDateTime, optionalEmail, optionalText, text, uuid } from "./common";

/* Public booking page and patient portal (no staff session). Everything is scoped by the clinic's slug. */

export type OnlineBookingMode = "off" | "requests" | "instant";

export interface PublicClinic {
  slug: string;
  name: string;
  phone: string | null;
  timeZone: string;
  locale: string;
  currency: string;
  /** What the booking page does now (instant falls back to requests while no code can be delivered). */
  booking: OnlineBookingMode;
  /** How a one-time code reaches the patient, or null when none can be sent. */
  verification: "email" | null;
  /** Portal sign-in needs a code sent to contact details on file. */
  portalAvailable: boolean;
  branches: { id: string; name: string; address: string | null }[];
  services: { id: string; name: string; durationMinutes: number }[];
  /** serviceIds empty = every online service. */
  doctors: { id: string; name: string; branchIds: string[]; serviceIds: string[] }[];
}

export const publicSlotsQuery = z.object({
  branchId: uuid,
  serviceId: uuid,
  doctorId: uuid.optional(),
  date: isoDate,
});

export interface PublicSlot {
  startsAt: string;
  doctorId: string;
}

export const otpRequestInput = z.object({
  purpose: z.enum(["booking", "portal"]),
  phone: z.string().trim().min(6).max(30),
  /** Where to send the code. Portal: must be the email already on the clinic's record for this phone. */
  email: optionalEmail,
});

export interface OtpRequestResult {
  challengeId: string;
  sentVia: "email";
  /** Masked address, e.g. a•••@gmail.com */
  sentTo: string;
}

export const otpVerification = z.object({ challengeId: uuid, code: z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code") });

export const publicBookingInput = z.object({
  branchId: uuid,
  serviceId: uuid,
  doctorId: uuid,
  startsAt: isoDateTime,
  fullName: text(120),
  phone: z.string().trim().min(6).max(30),
  email: optionalEmail,
  note: optionalText(500),
  verification: otpVerification.nullish().transform((v) => v ?? null),
  /** Honeypot: humans never see or fill it. */
  website: z.string().max(200).optional(),
});
export type PublicBookingInput = z.input<typeof publicBookingInput>;

export interface PublicBookingResult {
  status: "requested" | "booked";
  startsAt: string;
  doctorName: string;
  branchName: string;
  serviceName: string;
}

export interface PortalAppointment {
  id: string;
  startsAt: string;
  status: string;
  service: string | null;
  doctor: string;
  branch: string;
  canCancel: boolean;
}

export interface PortalInvoice {
  id: string;
  number: string | null;
  issueDate: string | null;
  totalMinor: number;
  balanceMinor: number;
  currency: string;
}

export interface PortalView {
  clinic: { name: string; phone: string | null };
  phone: string;
  email: string;
  payOnline: boolean;
  patients: {
    id: string;
    fullName: string;
    patientCode: string;
    appointments: PortalAppointment[];
    invoices: PortalInvoice[];
  }[];
}
